import React from "react";
import { Grid, Paper, Button } from "@material-ui/core";
import { OrderSideSelect } from "./OrderSideSelect";
import { DateInput } from "../../components/common/DateInput";
import { LocalizationContext } from "../../../core/localization/Localization";
import { ProductSelect } from "../../components/listing/ProductSelect";
import { OrderFilter } from "../../../data/models";
import { OrderStatusSelect } from "../../components/common/OrderStatusSelect";
import { ClientSelect } from "../../components/listing/ClientSelect";
import { useAuth } from "../../../data/auth/hooks";
import { isValidDate } from "../../../core/util/functions";
import { OrderFilterChipsComponent } from "./OrderFilterChips";
import { DisplayDrawerContext } from "../../../layout/DisplayDrawerContext";
import { OrderMarketSelect } from "./OrderMarketSelect";
import { EmployeeSelect } from "../../components/listing/EmployeeSelect";

export enum OrderFilterEditorType {
	Basic = 0,
	Extended = 1,
	Full = 2,
}

interface OrderFilterEditorProps {
	filter?: OrderFilter;
	editFilter: (prop: keyof OrderFilter, value: any) => void;
	save: () => void;
	type: OrderFilterEditorType;
	reset?: () => void;
}

export const OrderFilterEditor: React.FunctionComponent<OrderFilterEditorProps> = ({
	filter,
	editFilter,
	save,
	type,
	reset,
}) => {
	const showWithDrawer = React.useContext(DisplayDrawerContext);
	const { getString } = React.useContext(LocalizationContext);
	const [auth] = useAuth();

	return (
		<Paper
			style={{
				paddingLeft: "70px",
				paddingRight: "70px",
				paddingTop: "15px",
				paddingBottom: "15px",
			}}
		>
			<Grid container direction="column" spacing={6}>
				{type >= OrderFilterEditorType.Extended && (
					<Grid item>
						<OrderSideSelect
							handleChange={e => editFilter("side", e.target.value)}
							side={filter?.side}
							nullable
						/>
					</Grid>
				)}
				<Grid item>
					<OrderMarketSelect
						handleChange={e => editFilter("market", e.target.value)}
						market={filter?.market}
						nullable
					/>
				</Grid>
				<Grid item container direction="row" spacing={2}>
					<Grid item xs={12} sm={6}>
						<DateInput
							label={getString("orderFilterFrom")}
							value={filter?.fromDate || null}
							onChange={date => {
								editFilter("fromDate", isValidDate(date) ? date.toISOString() : undefined);
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DateInput
							label={getString("orderFilterTo")}
							value={filter?.toDate || null}
							onChange={date =>
								editFilter("toDate", isValidDate(date) ? date.toISOString() : undefined)
							}
						/>
					</Grid>
				</Grid>
				<Grid item>
					<ProductSelect
						label={getString("orderProduct")}
						predicate={prod => prod.prodId === filter?.product?.prodId}
						onChangeItem={prod => {
							editFilter("product", prod);
						}}
					/>
				</Grid>
				{type >= OrderFilterEditorType.Extended &&
					auth.status === "successful" &&
					auth.user.userIsEmployee === 1 && (
						<Grid item>
							<ClientSelect
								label={getString("orderClientName")}
								predicate={client => client.cliId === filter?.client?.cliId}
								onChangeItem={client => editFilter("client", client)}
							/>
						</Grid>
					)}
				{type >= OrderFilterEditorType.Extended && (
					<Grid item>
						<OrderStatusSelect
							label={getString("orderStatus")}
							predicate={status => status.ftValue === filter?.orderStatus?.ftValue}
							onChangeItem={status => {
								editFilter("orderStatus", status);
							}}
						/>
					</Grid>
				)}
				{type >= OrderFilterEditorType.Full && (
					<Grid item>
						<EmployeeSelect
							label={getString("orderbrokName")}
							predicate={employee => employee.empId === filter?.employee?.empId}
							onChangeItem={employee => {
								editFilter("employee", employee);
							}}
						/>
					</Grid>
				)}
				<Grid item container direction="row-reverse" spacing={2}>
					<Grid item>
						<Button
							variant="contained"
							color="secondary"
							onClick={() => {
								save();
								showWithDrawer(undefined);
							}}
						>
							{getString("orderFilterConfirm")}
						</Button>
					</Grid>
					<Grid item>
						<Button variant="text" onClick={reset}>
							{getString("filterReset")}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};

export const OrderFilterChips = OrderFilterChipsComponent;
